import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Loader2, MapPin, Layers, Package } from 'lucide-react';
import { GuideCard, PackCard, STUDIO_MAP_EMBED } from './GuidePages';
import { StudioOfflineBanner } from '../components/studio/StudioOfflineBanner';
import { TownAlertForm, CountryAlertForm } from '../components/studio/TownAlertForm';
import { regionSlug } from '../lib/regions';
import { useSeo } from '../lib/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const countryOf = (x) => x.country || 'Italy';
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const Eyebrow = ({ children }) => <p className="eyebrow mb-5"><span className="w-10 h-px bg-[#C5A059]" /> {children}</p>;

const Stat = ({ icon: Icon, children, testId }) => (
  <span className="text-sm text-[#E5C178] inline-flex items-center gap-2" data-testid={testId}><Icon size={16} /> {children}</span>
);

const RegionBlock = ({ region, guides }) => (
  <div className="mb-14" data-testid={`country-region-${regionSlug(region)}`}>
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-4 border-b border-[#E6DAC5]">
      <div>
        <h3 className="font-display text-3xl text-[#1E1622]">{region}</h3>
        <p className="text-sm text-[#1E1622]/55 mt-1">{plural(guides.length, 'town guide')}</p>
      </div>
      <Link to={`/shop/regions/${regionSlug(region)}`} data-testid={`country-region-link-${regionSlug(region)}`} className="link-gold inline-flex items-center gap-1 text-sm">
        Explore {region} <ArrowRight size={14} />
      </Link>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{guides.map((g) => <GuideCard key={g.id} g={g} />)}</div>
  </div>
);

const Hero = ({ country, intro, image, towns, regions, packs }) => (
  <section className="relative pt-32 pb-16 bg-[#2D1037] overflow-hidden">
    {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
    <div className="absolute inset-0 bg-gradient-to-r from-[#2D1037] via-[#2D1037]/90 to-transparent" />
    <div className="relative container-custom">
      <Link to="/shop#small-towns-series" data-testid="country-back-link" className="flex w-fit items-center gap-2 text-sm text-[#E5C178] mb-8 hover:text-[#C5A059]"><ArrowLeft size={16} /> All countries</Link>
      <span className="eyebrow mb-4 text-[#C5A059]"><span className="w-10 h-px bg-[#C5A059]" /> Small Towns Series</span>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#FAF7F2] mb-5" data-testid="country-title">Small Towns of {country}</h1>
      <p className="max-w-2xl text-[#FAF7F2]/75 font-light leading-relaxed mb-8 whitespace-pre-line" data-testid="country-intro">{intro}</p>
      <div className="flex flex-wrap items-center gap-6">
        <Stat icon={MapPin} testId="country-stat-towns">{plural(towns, 'town guide')}</Stat>
        <Stat icon={Layers} testId="country-stat-regions">{plural(regions, 'region')}</Stat>
        {packs > 0 && <Stat icon={Package} testId="country-stat-packs">{plural(packs, 'regional pack')}</Stat>}
      </div>
    </div>
  </section>
);

const OtherCountries = ({ countries, current }) => {
  const others = countries.filter((c) => c !== current);
  if (!others.length) return null;
  return (
    <div className="mb-16" data-testid="other-countries">
      <Eyebrow>More in the Small Towns Series</Eyebrow>
      <div className="flex flex-wrap gap-2">
        {others.map((c) => (
          <Link key={c} to={`/shop/countries/${regionSlug(c)}`} data-testid={`other-country-${regionSlug(c)}`}
            className="px-4 py-2 rounded-full text-sm border border-[#E6DAC5] bg-white text-[#1E1622]/70 transition-colors duration-200 hover:border-[#C5A059] hover:text-[#3B164C]">{c}</Link>
        ))}
      </div>
    </div>
  );
};

export default function CountryPage() {
  const { slug } = useParams();
  const [guides, setGuides] = useState(null);
  const [packs, setPacks] = useState([]);
  const [intro, setIntro] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/guides`).then((r) => setGuides(r.data.guides || [])).catch(() => setGuides([]));
    axios.get(`${API}/studio-packs`).then((r) => setPacks(r.data.packs || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load, slug]);
  useEffect(() => { axios.get(`${API}/country-pages/${slug}`).then((r) => setIntro(r.data.intro)).catch(() => setIntro('')); }, [slug]);

  const inCountry = (guides || []).filter((g) => regionSlug(countryOf(g)) === slug);
  const country = inCountry[0] ? countryOf(inCountry[0]) : null;
  const regions = Array.from(new Set(inCountry.map((g) => g.region).filter(Boolean))).sort();
  const countryPacks = packs.filter((p) => p.region && regions.includes(p.region) && (!p.country || regionSlug(p.country) === slug));
  const countries = Array.from(new Set((guides || []).map(countryOf))).sort();
  const image = inCountry[0]?.cover_image_url;

  useSeo(country && { title: `Small Towns of ${country}: Town Guides & Regional Packs for Australians`, description: (intro || '').slice(0, 160), image }, [country, intro]);

  if (!guides) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!country) {
    return (
      <div className="min-h-screen pt-32 container-custom text-center" data-testid="country-not-found">
        <StudioOfflineBanner onBack={load} />
        <p className="text-[#1E1622]/60 mb-10">The Small Towns Series doesn't cover this country yet.</p>
        <div className="max-w-2xl mx-auto mb-6"><CountryAlertForm exclude={countries} /></div>
        <Link to="/shop" className="link-gold inline-block">Browse the shop</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" data-testid="country-page">
      <Hero country={country} intro={intro} image={image} towns={inCountry.length} regions={regions.length} packs={countryPacks.length} />
      <div className="container-custom pt-12">
        <StudioOfflineBanner onBack={load} />
        <div className="rounded-2xl overflow-hidden border border-[#E6DAC5] mb-16" data-testid="country-map">
          <iframe src={`${STUDIO_MAP_EMBED}?country=${encodeURIComponent(country)}`} title={`Map of small towns in ${country}`} loading="lazy" style={{ width: '100%', height: 480, border: 0 }} />
        </div>
        {countryPacks.length > 0 && (
          <section className="mb-16" data-testid="country-packs">
            <Eyebrow>Regional packs · every town in a region, together</Eyebrow>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{countryPacks.map((p) => <PackCard key={p.id} p={p} />)}</div>
          </section>
        )}
        <section data-testid="country-regions">
          <Eyebrow>Town guides by region</Eyebrow>
          {regions.map((r) => <RegionBlock key={r} region={r} guides={inCountry.filter((g) => g.region === r)} />)}
        </section>
        <div className="max-w-2xl mb-16"><TownAlertForm regions={regions} initialRegions={[]} dark title={`New towns in ${country}`} /></div>
        <OtherCountries countries={countries} current={country} />
      </div>
    </div>
  );
}
